const productSections = new WeakMap();
const productMediaPlayback = new WeakMap();

const pauseProductMedia = (section, exceptMediaId = null) => {
  section.querySelectorAll('.product__media-container [data-media-id]').forEach((media) => {
    if (exceptMediaId && media.dataset.mediaId === exceptMediaId) return;

    media.querySelectorAll('video').forEach((video) => video.pause());
    media.querySelectorAll('iframe').forEach((frame) => {
      const src = frame.getAttribute('src') || '';
      const command = src.includes('youtube.com') || src.includes('youtu.be')
        ? { event: 'command', func: 'pauseVideo', args: [] }
        : src.includes('vimeo.com')
          ? { method: 'pause' }
          : null;

      if (command) frame.contentWindow?.postMessage(JSON.stringify(command), '*');
    });
  });
};

const initProductMediaPlayback = (section) => {
  if (productMediaPlayback.has(section)) return;

  // Shopify can render the shop domain here even on preview or local URLs.
  // YouTube only accepts player commands from the configured page origin.
  section.querySelectorAll('.product__media-container iframe').forEach((frame) => {
    const url = new URL(frame.src);
    if (!['www.youtube.com', 'www.youtube-nocookie.com'].includes(url.hostname)) return;
    if (url.searchParams.get('origin') === window.location.origin) return;
    url.searchParams.set('origin', window.location.origin);
    frame.src = url.href;
  });

  const onThumbnailClick = (event) => {
    const thumbnail = event.target?.closest?.('[data-product-thumbnail]');
    if (!thumbnail || !section.contains(thumbnail)) return;
    pauseProductMedia(section, thumbnail.dataset.thumbnailId);
  };

  const cleanup = () => {
    pauseProductMedia(section);
    document.removeEventListener('click', onThumbnailClick, true);
  };

  document.addEventListener('click', onThumbnailClick, true);
  productMediaPlayback.set(section, cleanup);
};

const initDependentOptions = (section) => {
  if (productSections.has(section)) return;

  const picker = section.querySelector('[data-hide-unavailable-dependent-options="true"]');
  if (!picker || picker.dataset.enableDynamicProductOptions === 'false') return;

  const variantsNode = picker.querySelector('[data-variant-json]');
  if (!variantsNode) return;
  const variants = JSON.parse(variantsNode.textContent);
  const inputs = Array.from(picker.querySelectorAll('.dynamic-variant-input'));

  const sync = () => {
    inputs.forEach((input, index) => {
      const matching = variants.filter((variant) => inputs.slice(0, index).every((parent, parentIndex) => (
        variant.options[parentIndex] === parent.value
      )));
      const values = new Set(matching.map((variant) => variant.options[index]));
      if (values.size && !values.has(input.value)) {
        const option = Array.from(input.options).find((candidate) => values.has(candidate.value));
        if (option) {
          input.value = option.value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      const group = Array.from(picker.querySelectorAll('.dynamic-variant-input-wrap')).find((node) => (
        node.dataset.index === input.dataset.index
      ));
      group?.querySelectorAll('.dynamic-variant-button').forEach((button) => {
        button.hidden = !values.has(button.dataset.optionValue);
        button.classList.toggle('selected', button.dataset.optionValue === input.value);
      });
    });
  };

  const onChange = (event) => {
    if (inputs.includes(event.target)) sync();
  };
  picker.addEventListener('change', onChange, true);
  productSections.set(section, () => picker.removeEventListener('change', onChange, true));
  sync();
};


const optionGroups = new WeakMap();
const initOptionGroups = (section) => {
  if (optionGroups.has(section) || !section.querySelector('.product__color-swatches--tenon')) return;
  const controller = new AbortController();
  const timers = new Set();
  function manageTenonGroup(groupSelector) {
    var container = section.querySelector(groupSelector);
    var cards = Array.from(section.querySelectorAll(groupSelector + ' .swatches_options'));

    if (!cards.length) {
      return;
    }

    var selectedCard = cards.find(function(card) {
      return card.querySelector('.product__color-swatch--tenon-card.selected');
    });

    cards.forEach(function(card) {
      card.style.display = 'none';
    });

    (selectedCard || cards[0]).style.display = 'block';

    if (container) {
      container.classList.toggle('is-active', !!selectedCard);
    }
  }

  function syncFlowKitDetails() {
    var selectedFlowKit = section.querySelector('.have-accessories-container .product__color-swatch--tenon-card.selected');
    var flowKitContainer = section.querySelector('.have-accessories-container');
    var selectedOptionValue = selectedFlowKit ? selectedFlowKit.getAttribute('data-option-value') : '';
    var modules = Array.from(section.querySelectorAll('[data-flow-kit-details]'));

    if (!modules.length) {
      return;
    }

    modules.forEach(function(module, index) {
      var shouldShow = flowKitContainer && flowKitContainer.classList.contains('is-active') && (selectedOptionValue
        ? module.getAttribute('data-option-value') === selectedOptionValue
        : index === 0);

      module.hidden = !shouldShow;
    });
  }

  function syncTenonOptionGroups() {
    manageTenonGroup('.no-accessories');
    manageTenonGroup('.have-accessories-container');
    syncFlowKitDetails();
  }

  syncTenonOptionGroups();

  section.querySelectorAll('.product__color-swatch--tenon-card, .product__color-swatch--tenon-toggle').forEach(function(button) {
    button.addEventListener('click', function() {
      const timer = setTimeout(() => { timers.delete(timer); syncTenonOptionGroups(); }, 50);
      timers.add(timer);
    }, { signal: controller.signal });
  });

  optionGroups.set(section, () => { controller.abort(); timers.forEach(clearTimeout); });
};

const init = (root) => {
  const sections = [];
  if (root.matches?.('[data-section-type="product"]')) sections.push(root);
  sections.push(...root.querySelectorAll('[data-section-type="product"]'));
  sections.forEach((section) => {
    initOptionGroups(section);
    initDependentOptions(section);
    initProductMediaPlayback(section);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
} else {
  init(document);
}

document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const unload = (section) => {
    optionGroups.get(section)?.();
    optionGroups.delete(section);
    productSections.get(section)?.();
    productSections.delete(section);
    productMediaPlayback.get(section)?.();
    productMediaPlayback.delete(section);
  };
  if (event.target.matches('[data-section-type="product"]')) unload(event.target);
  event.target.querySelectorAll('[data-section-type="product"]').forEach(unload);
});
